<template>
  <v-dialog v-model="dialog" width="800" :persistent="true">
    <template v-slot:activator="{ on }">
      <v-btn color="primary" v-on="on">
        {{ 'app.add' | trans }}
      </v-btn>
    </template>

    <v-card>
      <v-toolbar card dark color="primary">
        <v-toolbar-title>{{ 'app.discount_codes.discount_codes' | trans }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn icon dark small @click="close">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text class="pt-0">
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-container grid-list-md no-padding>
            <v-layout row wrap>
              <v-flex xs6>
                <v-text-field
                  v-model="discountCode.name"
                  :label="'app.discount_codes.name' | trans"
                  :rules="rules.name"
                  :readonly="!isEditable"
                ></v-text-field>
              </v-flex>
              <v-flex xs6>
                <v-text-field
                  v-model="discountCode.code"
                  :label="'app.discount_codes.code' | trans"
                  :rules="rules.code"
                  :readonly="!isEditable"
                ></v-text-field>
              </v-flex>
              <v-flex xs3>
                <v-menu
                  v-model="startDatePicker"
                  :close-on-content-click="true"
                  :nudge-right="40"
                  :readonly="!isEditable"
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="discountCode.startDate"
                      readonly
                      :label="trans('app.discount_codes.start_date')"
                      v-on="on"
                      :rules="rules.startDate"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="discountCode.startDate"
                    @input="startDatePicker = false"
                    locale="pl-pl"
                    :min="moment().format(SYSTEM_DATE)"
                    :readonly="!isEditable"
                  ></v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex xs3>
                <v-text-field
                  v-model="discountCode.startDatetime"
                  :label="'app.discount_codes.start_datetime' | trans"
                  :readonly="true"
                  @click="startDatetime = !startDatetime"
                  :rules="rules.startDateTime"
                ></v-text-field>
              </v-flex>
              <v-flex xs3>
                <v-menu
                  v-model="endDatePicker"
                  :close-on-content-click="true"
                  :nudge-right="40"
                  :readonly="!isEditable"
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="discountCode.endDate"
                      readonly
                      :clearable="true"
                      v-on="on"
                      :label="trans('app.discount_codes.end_date')"
                      :rules="rules.endDate"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="discountCode.endDate"
                    @input="endDatePicker = false"
                    locale="pl-pl"
                    :min="moment().format(SYSTEM_DATE)"
                    :readonly="!isEditable"
                  ></v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex xs3>
                <v-text-field
                  v-model="discountCode.endDatetime"
                  :label="'app.discount_codes.end_datetime' | trans"
                  :readonly="true"
                  @click="endDatetime = !endDatetime"
                  :rules="rules.endDateTime"
                  :clearable="true"
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <div class="v-messages theme--light error--text" v-if="datesError !== null">
                  <div class="v-messages__wrapper">
                    <div class="v-messages__message">
                      {{ datesError }}
                    </div>
                  </div>
                </div>
              </v-flex>
              <v-flex xs4>
                <v-text-field
                  v-model="discountCode.limit"
                  :label="'app.discount_codes.limit' | trans"
                  :rules="rules.limit"
                  :readonly="!isEditable"
                ></v-text-field>
              </v-flex>
              <v-flex xs4>
                <v-select
                  v-model="discountCode.type"
                  :items="availableTypes"
                  :label="'app.discount_codes.type' | trans"
                  item-text="label"
                  item-value="value"
                  :readonly="!isEditable"
                ></v-select>
              </v-flex>
              <v-flex xs4>
                <v-text-field
                  v-model="discountCode.value"
                  :label="'app.discount_codes.value' | trans"
                  :rules="rules.value"
                  :suffix="suffix"
                  :readonly="!isEditable"
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-checkbox
                  :color="`success`"
                  :label="'app.discount_codes.all' | trans"
                  :readonly="!isEditable"
                  v-model="allCheckbox"
                  class="no-margin"
                ></v-checkbox>
              </v-flex>
              <v-flex xs12>
                <v-checkbox
                  :color="`success`"
                  :disabled="allCheckbox"
                  :readonly="!isEditable"
                  v-model="newUserCheckbox"
                  :label="'app.discount_codes.new_user' | trans"
                  class="no-margin"
                ></v-checkbox>
              </v-flex>
              <v-flex xs6>
                <v-checkbox
                  :color="`success`"
                  :disabled="allCheckbox"
                  :readonly="!isEditable"
                  v-model="roleCheckbox"
                  label="Role"
                  class="no-margin"
                ></v-checkbox>
              </v-flex>
              <v-flex xs6>
                <v-select
                  :items="translatedRoles"
                  :disabled="!roleCheckbox"
                  :readonly="!isEditable"
                  v-model="discountCode.roles"
                  item-text="name"
                  item-value="id"
                  multiple
                  class="no-margin no-padding"
                ></v-select>
              </v-flex>
              <v-flex xs6>
                <v-checkbox
                  :color="`success`"
                  :disabled="allCheckbox"
                  :readonly="!isEditable"
                  v-model="userCheckbox"
                  :label="'app.discount_codes.users' | trans"
                  class="no-margin"
                ></v-checkbox>
              </v-flex>
              <v-flex xs6>
                <v-select
                  :items="users"
                  :disabled="!userCheckbox"
                  :readonly="!isEditable"
                  v-model="discountCode.users"
                  multiple
                  item-text="name"
                  item-value="id"
                  class="no-margin no-padding"
                ></v-select>
              </v-flex>
              <v-flex xs6>
                <v-checkbox
                  :color="`success`"
                  :disabled="allCheckbox"
                  :readonly="!isEditable"
                  v-model="subscriptionCheckbox"
                  :label="'app.discount_codes.subscriptions' | trans"
                  class="no-margin"
                ></v-checkbox>
              </v-flex>
              <v-flex xs6>
                <v-select
                  :items="subscriptions"
                  :disabled="!subscriptionCheckbox"
                  :readonly="!isEditable"
                  v-model="discountCode.subscriptions"
                  item-text="name"
                  item-value="id"
                  multiple
                  class="no-margin no-padding"
                ></v-select>
              </v-flex>
              <v-flex xs12>
                <div class="v-messages theme--light error--text" v-if="checkboxError !== null">
                  <div class="v-messages__wrapper">
                    <div class="v-messages__message">
                      {{ checkboxError }}
                    </div>
                  </div>
                </div>
              </v-flex>
            </v-layout>
          </v-container>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" flat @click="close">
          {{ 'app.close' | trans }}
        </v-btn>
        <v-btn v-if="isEditable" :loading="loading" color="primary" @click="save" :disabled="!valid">
          {{ 'app.save' | trans }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-dialog v-model="startDatetime" lazy hide-overlay full-width max-width="290px" min-width="290px">
      <v-time-picker
        v-if="startDatetime"
        format="24hr"
        v-model="discountCode.startDatetime"
        @click:minute="startDatetime = false"
      ></v-time-picker>
    </v-dialog>
    <v-dialog v-model="endDatetime" lazy hide-overlay full-width max-width="290px" min-width="290px">
      <v-time-picker
        format="24hr"
        v-if="endDatetime"
        v-model="discountCode.endDatetime"
        @click:minute="endDatetime = false"
      ></v-time-picker>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { trans } from '../../app';
import moment from 'moment';
import RulesBuilder from '../../utils/rulesBuilder';
import {MODE} from "./mode/mode";
import {
  getRulesFromDiscountCodeData,
  ROLE_TYPE,
  USER_TYPE,
  FOR_ALL_TYPE,
  SUBSCRIPTION_TYPE,
  NEW_USER_TYPE,
} from './getRulesFromDiscountCodeData';
import { mapActions } from 'vuex';
import {SYSTEM_DATE} from "../../utils/dateFormatter";

export default {
  computed: {
    moment() {
      return moment;
    },
    isEditable() {
      return this.mode === MODE.EDIT;
    },
    rules: function () {
      return {
        name: [
          ...this.rulesBuilder
            .addRequiredRule(trans('app.discount_codes.name'))
            .addMinLengthRule(trans('app.discount_codes.name'), 1)
            .addMaxLengthRule(trans('app.discount_codes.name'), 255)
            .getRules(),
        ],
        code: [
          ...this.rulesBuilder
            .addRequiredRule(trans('app.discount_codes.code'))
            .addMinLengthRule(trans('app.discount_codes.code'), 1)
            .addMaxLengthRule(trans('app.discount_codes.code'), 255)
            .getRules(),
        ],
        startDate: [
          ...this.rulesBuilder
            .addRequiredRule(trans('app.discount_codes.start_date'))
            .addDateFormatRule(trans('app.discount_codes.start_date'), SYSTEM_DATE)
            .getRules(),
        ],
        startDateTime: [
          ...this.rulesBuilder
            .addRequiredRule(trans('app.discount_codes.start_datetime'))
            .addTimeRule(trans('app.discount_codes.start_datetime'), SYSTEM_DATE)
            .getRules(),
        ],
        endDate: [
          ...this.rulesBuilder
            .allowEmptyRule()
            .addDateFormatRule(trans('app.discount_codes.end_date'), SYSTEM_DATE)
            .getRules(),
        ],
        endDateTime: [
          ...this.rulesBuilder.allowEmptyRule().addTimeRule(trans('app.discount_codes.end_datetime')).getRules(),
        ],
        limit: [...this.rulesBuilder.allowEmptyRule().addIsIntegerRule(trans('app.discount_codes.limit')).getRules()],
        value: [
          ...this.rulesBuilder
            .addRequiredRule(trans('app.discount_codes.value'))
            .addIsNumericRule(trans('app.discount_codes.value'))
            .addNumberBiggerThanRule(trans('app.discount_codes.value'), 0)
            .getRules(),
        ],
      };
    },
    suffix() {
      return this.discountCode.type === 'fixed' ? 'zł' : '%';
    },
    SYSTEM_DATE() {
      return SYSTEM_DATE;
    }
  },
  data: function () {
    return {
      mode: MODE.EDIT,
      datesError: null,
      checkboxError: null,
      availableTypes: [
        { label: trans('app.discount_codes.fixed_type'), value: 'fixed' },
        { label: trans('app.discount_codes.percentage_type'), value: 'percentage' },
      ],
      dialog: false,
      loading: false,
      valid: false,
      startDatePicker: false,
      startDatetime: false,
      endDatePicker: false,
      endDatetime: false,
      allCheckbox: false,
      roleCheckbox: false,
      userCheckbox: false,
      subscriptionCheckbox: false,
      newUserCheckbox: false,
      discountCode: {
        id: null,
        name: '',
        code: '',
        startDate: '',
        startDatetime: '',
        endDate: '',
        endDatetime: '',
        limit: null,
        users: [],
        roles: [],
        subscriptions: [],
        type: 'fixed',
        value: null,
      },
      rulesBuilder: new RulesBuilder(),
      translatedRoles: [],
    };
  },
  mounted: function() {
    this.translatedRoles = this.roles.map((role) => {
      role.name = trans('page.roles.' + role.name);
      return role;
    })
  },
  props: {
    users: Array,
    roles: Array,
    subscriptions: Array,
  },
  methods: {
    ...mapActions(['fetchDiscountCodes']),
    close() {
      this.mode = MODE.EDIT;
      this.dialog = false;
      this.datesError = null;
      this.checkboxError = null;
      this.$refs.form.reset();
      this.allCheckbox = false;
      this.roleCheckbox = false;
      this.userCheckbox = false;
      this.subscriptionCheckbox = false;
      this.newUserCheckbox = false;
      this.discountCode.id = null;
      setTimeout(() => (this.discountCode.type = 'fixed'), 1000);
    },
    openAndEdit(discountCode, mode) {
      this.mode = mode;
      this.dialog = true;
      this.discountCode.id = discountCode.id;
      this.discountCode.name = discountCode.name;
      this.discountCode.code = discountCode.code;
      this.discountCode.startDate = discountCode.start_date.slice(0, 10);
      this.discountCode.startDatetime = discountCode.start_date.slice(11, 16);
      if (discountCode.end_date !== null) {
        this.discountCode.endDate = discountCode.end_date.slice(0, 10);
        this.discountCode.endDatetime = discountCode.end_date.slice(11, 16);
      }
      this.discountCode.limit = discountCode.limit;
      this.discountCode.type = discountCode.type;
      this.discountCode.value = discountCode.value;
      discountCode.rules.forEach((rule) => {
        if (rule.type === FOR_ALL_TYPE) {
          this.allCheckbox = true;
        } else if (rule.type === ROLE_TYPE) {
          this.discountCode.roles.push(rule.value);
          this.roleCheckbox = true;
        } else if (rule.type === USER_TYPE) {
          this.discountCode.users.push(rule.value);
          this.userCheckbox = true;
        } else if (rule.type === SUBSCRIPTION_TYPE) {
          this.discountCode.subscriptions.push(rule.value);
          this.subscriptionCheckbox = true;
        } else if (rule.type === NEW_USER_TYPE) {
          this.newUserCheckbox = true;
        }
      });
    },
    async save() {
      if (
        this.$refs.form.validate() === false ||
        this.validatesDates() === false ||
        this.validateCheckboxes() === false
      ) {
        return;
      }

      this.loading = true;
      const endDate =
        !!this.discountCode.endDate !== false
          ? `${this.discountCode.endDate} ${this.discountCode.endDatetime}:00`
          : null;
      const discountCode = {
        name: this.discountCode.name,
        code: this.discountCode.code,
        start_date: `${this.discountCode.startDate} ${this.discountCode.startDatetime}:00`,
        end_date: endDate,
        limit: this.discountCode.limit,
        type: this.discountCode.type,
        value: this.discountCode.value,
        rules: getRulesFromDiscountCodeData(
          this.allCheckbox,
          this.userCheckbox,
          this.roleCheckbox,
          this.subscriptionCheckbox,
          this.discountCode.users,
          this.discountCode.roles,
          this.discountCode.subscriptions,
          this.newUserCheckbox
        ),
      };

      try {
        if (this.discountCode.id === null) {
          await axios.post('/v2/discount-codes', discountCode);
        } else {
          await axios.put(`/v2/discount-codes/${this.discountCode.id}`, discountCode);
        }
        this.$toast.success(
          trans('app.actions.saved', { name: trans('app.discount_codes.discount_code').toLowerCase() })
        );
        this.loading = false;
      } catch (e) {
        let message = trans('app.save_failed');
        if (e.response
            && e.response.status
            && +e.response.status === 422
            && e.response.data
            && e.response.data.message) {
          message = e.response.data.message;
        } else {
          this.sentryCaptureException(e);
        }

        this.$toast.error(message);
        this.loading = false;
        return;
      }
      await this.fetchDiscountCodes();
      this.close();
    },
    validatesDates() {
      const startDate =
        !!this.discountCode.startDate !== false
          ? `${this.discountCode.startDate} ${this.discountCode.startDatetime}:00`
          : null;
      const endDate =
        !!this.discountCode.endDate !== false
          ? `${this.discountCode.endDate} ${this.discountCode.endDatetime}:00`
          : null;

      if (null === startDate || null === endDate) {
        return true;
      }

      if (moment(startDate).isAfter(moment(endDate))) {
        this.datesError = trans('validation.date_is_after', { attribute: trans('app.discount_codes.end_date') });

        return false;
      }
      this.datesError = null;

      return true;
    },
    validateCheckboxes() {
      if (
        this.discountCode.roles.length === 0 &&
        this.discountCode.users.length === 0 &&
        this.discountCode.subscriptions.length === 0 &&
        this.allCheckbox === false &&
        this.newUserCheckbox === false
      ) {
        this.checkboxError = trans('app.discount_codes.missing_constraint');

        return false;
      }
      this.checkboxError = null;

      return true;
    },
  },
  watch: {
    allCheckbox() {
      this.userCheckbox = false;
      this.roleCheckbox = false;
      this.subscriptionCheckbox = false;
      this.newUserCheckbox = false;
      this.discountCode.users = [];
      this.discountCode.roles = [];
      this.discountCode.subscriptions = [];
    },
  },
};
</script>

<style scoped></style>
